import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight, ImageIcon } from 'lucide-react'
import { CmsImage } from '@/components/generals/cms-image'

interface ProjectCardProps {
    project: ProjectSummary
    preload?: boolean
}

export const ProjectCard: FC<ProjectCardProps> = ({ project, preload = false }): ReactNode => {
    return (
        <article className='group'>
            <Link
                href={`/projects/${project.slug}`}
                className='block rounded-2xl outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-offset-4 focus-visible:ring-offset-background'
            >
                <div className='relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted'>
                    {project.cover
                        ? (
                            <CmsImage
                                src={project.cover}
                                alt={project.cover_alt ?? ''}
                                sizes='(min-width: 1152px) 528px, (min-width: 768px) 50vw, 100vw'
                                preload={preload}
                                className='transition-transform duration-700 ease-out motion-safe:group-hover:scale-[1.03]'
                            />
                        )
                        : (
                            <div className='absolute inset-0 flex items-center justify-center text-muted-foreground'>
                                <ImageIcon aria-hidden='true' className='size-6' />
                            </div>
                        )}
                </div>

                <div className='mt-5 flex items-start justify-between gap-6'>
                    <div className='min-w-0'>
                        <h3
                            data-mdlfy-cms={`projects:${project.id}:title`}
                            className='font-heading text-3xl leading-tight'
                        >
                            {project.title}
                        </h3>

                        {project.summary && (
                            <p
                                data-mdlfy-cms={`projects:${project.id}:summary`}
                                className='mt-2 text-muted-foreground'
                            >
                                {project.summary}
                            </p>
                        )}
                    </div>

                    <span
                        aria-hidden='true'
                        className='mt-1 inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-border transition-colors duration-300 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground'
                    >
                        <ArrowUpRight className='size-4' />
                    </span>
                </div>

                {(project.category || project.year) && (
                    <p className='mt-4 flex flex-wrap items-center gap-x-2 font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground'>
                        {project.category && (
                            <span data-mdlfy-cms={`projects:${project.id}:category`}>{project.category}</span>
                        )}

                        {project.category && project.year && <span aria-hidden='true'>/</span>}

                        {project.year && (
                            <span data-mdlfy-cms={`projects:${project.id}:year`}>{project.year}</span>
                        )}
                    </p>
                )}
            </Link>
        </article>
    )
}
